import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Clock, X, RefreshCw, Mail, Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface Invitation {
  id: string;
  email: string;
  role: string;
  status: string;
  created_at: string;
  expires_at: string;
}

export function PendingInvitations() {
  const { organization } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: invitations, isLoading } = useQuery({
    queryKey: ['pending-invitations', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];
      
      const { data, error } = await supabase
        .from('org_invitations')
        .select('*')
        .eq('org_id', organization.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Invitation[];
    },
    enabled: !!organization?.id,
  });

  const cancelMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      const { error } = await supabase
        .from('org_invitations')
        .update({ status: 'cancelled' })
        .eq('id', invitationId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: 'Invitation cancelled',
        description: 'The invitation has been cancelled',
      });
      queryClient.invalidateQueries({ queryKey: ['pending-invitations'] });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to cancel invitation',
        variant: 'danger',
      });
    },
  });

  const resendMutation = useMutation({
    mutationFn: async ({ email, role }: { email: string; role: string }) => {
      const response = await supabase.functions.invoke('send-invitation', {
        body: { email, role },
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      if (response.data?.error) {
        throw new Error(response.data.error);
      }

      return response.data;
    },
    onSuccess: () => {
      toast({
        title: 'Invitation resent',
        description: 'A new invitation has been sent',
      });
      queryClient.invalidateQueries({ queryKey: ['pending-invitations'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to resend invitation',
        variant: 'danger',
      });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!invitations || invitations.length === 0) {
    return null;
  }

  const isExpired = (expiresAt: string) => new Date(expiresAt) < new Date();

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
        <Clock className="h-4 w-4" />
        Pending Invitations ({invitations.length})
      </h4>
      
      {invitations.map((invitation) => {
        const expired = isExpired(invitation.expires_at);
        
        return (
          <div 
            key={invitation.id} 
            className={`flex items-center justify-between p-3 border rounded-lg ${expired ? 'bg-muted/50 opacity-75' : ''}`}
          >
            <div className="flex items-center gap-3">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="font-medium">{invitation.email}</p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Badge variant="outline" className="capitalize">
                    {invitation.role}
                  </Badge>
                  {expired ? (
                    <span className="text-danger">Expired</span>
                  ) : (
                    <span>
                      Expires {formatDistanceToNow(new Date(invitation.expires_at), { addSuffix: true })}
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  // Cancel old invitation first, then resend
                  cancelMutation.mutate(invitation.id, {
                    onSuccess: () => {
                      resendMutation.mutate({ 
                        email: invitation.email, 
                        role: invitation.role 
                      });
                    }
                  });
                }}
                disabled={resendMutation.isPending || cancelMutation.isPending}
              >
                <RefreshCw className={`h-4 w-4 ${resendMutation.isPending ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => cancelMutation.mutate(invitation.id)}
                disabled={cancelMutation.isPending}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
